import {
  createContext,
  FlowProps,
  JSX,
  onCleanup,
  Owner,
  runWithOwner,
} from "solid-js";
import c from "class-c";
import {
  loadStripe,
  type Stripe,
  StripeElement,
  StripeElements,
  StripeElementType,
  StripePaymentElement,
  StripePaymentElementChangeEvent,
} from "@stripe/stripe-js";

import createAsync from "@repo/signals/createAsync";
import Locale from "@repo/utils/Locale";
import UBoolean from "@repo/utils/UBoolean";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import UProxy from "@repo/utils/UProxy";
import UString from "@repo/utils/UString";
import Platform from "@repo/utils-client/Platform";
import UAccessor from "@repo/utils-solid/UAccessor";
import USolid from "@repo/utils-solid/USolid";

import { useError } from "../error/ErrorProvider";
import Spinner from "../loaders/Spinner";
import createDomRect from "../signals/createDomRect";

namespace Stripe {
  export const load = UFunction.memo((publicKey: string) =>
    loadStripe(publicKey, { locale: Locale.current })
      .then((client) => {
        if (client == null) throw new Error("Failed to load Stripe.");
        return client;
      })
      .catch((e) => {
        // Invalidate memo cache to attempt next time
        load.invalidate(publicKey);
        throw e;
      })
  );

  const Context = createContext<{
    key: string;
    load(): Promise<Stripe>;
    optionsFor: { payment?: Options<"payment"> };
  }>();
  export function Provider({
    key,
    optionsFor = {},
    children,
  }: D<
    FlowProps<{
      optionsFor?: NonNullable<typeof Context.defaultValue>["optionsFor"];
      key: string;
    }>
  >) {
    return (
      <Context.Provider value={{ key, optionsFor, load: () => load(key) }}>
        {children}
      </Context.Provider>
    );
  }

  export function useContext() {
    const context = USolid.useContext(Context);

    if (!context)
      throw new Error(
        "Cannot call Stripe.useContext outside of Stripe.Provider."
      );

    return context;
  }

  type Events = "ready" | "change" | "focus" | "blur" | "escape";
  type EventsFor<Element extends StripeElement> = {
    [Event in Events as `on${UString.Capitalize<Event>}`]?: Element extends {
      on(eventType: Event, handler: (event: infer E) => any): Element;
    }
      ? (event: E) => any
      : never;
  };

  export type Elements = StripeElements;

  export namespace Elements {
    const Context = createContext<
      (() => Elements | null) & { clientSecret(): string | null }
    >();
    export declare namespace Provder {
      type Props = {
        clientSecret: string | null;
        children: JSX.Element;
      };
    }
    export function Provider({ clientSecret, children }: D<Provder.Props>) {
      const error = useError();

      const stripe = Stripe.useContext();

      const elements = createAsync(() => {
        if (!Platform.isClient) return null;

        return stripe
          .load()
          .then(async (stripe) =>
            stripe.elements({
              locale: Locale.current,
              clientSecret: await UAccessor.waitUntil(
                () => clientSecret,
                UBoolean.isTruthy
              ),
            })
          )
          .catch((e) => {
            error.capture(e);
            throw e;
          });
      }, null);

      return (
        <Context.Provider
          value={Object.assign(elements, { clientSecret: () => clientSecret })}
        >
          {children}
        </Context.Provider>
      );
    }
    export function useContext() {
      const context = USolid.useContext(Context);

      if (!context)
        throw new Error(
          "Cannot call Stripe.Elements.useContext outside of Stripe.Elements.Provider."
        );

      return context;
    }
  }

  type Options<Type extends StripeElementType> =
    Extract<
      StripeElement,
      {
        on(
          eventType: string,
          handler: (event: { elementType: Type }) => any
        ): any;
      }
    > extends { update(options: infer Options): any }
      ? Options
      : never;

  const mountStripeElement =
    <Type extends StripeElementType>(
      type: Type,
      options: Options<Type>,
      onEvent: (
        callbackName:
          | "onChange"
          | "onReady"
          | "onFocus"
          | "onBlur"
          | "onEscape",
        e: any
      ) => void
    ) =>
    (ref: HTMLDivElement) => {
      const stripe = Stripe.useContext();
      const elements = UAccessor.waitUntil(
        Elements.useContext(),
        UBoolean.isTruthy
      );

      elements.then((elements) => {
        const element = elements.create(
          type as any,
          UProxy.merge((stripe.optionsFor as any)[type] || {}, options as any)
        );

        element.on("change", (e) => onEvent?.("onChange", e));
        element.on("ready", (e) => onEvent?.("onReady", e));
        element.on("focus", (e) => onEvent?.("onFocus", e));
        element.on("blur", (e) => onEvent?.("onBlur", e));
        element.on("escape", (e) => onEvent?.("onEscape", e));

        element.mount(ref);

        return elements;
      });

      onCleanup(async () => {
        (await elements).getElement(type as any)?.destroy();
      });
    };

  export declare namespace PaymentElement {
    type Props = UObject.Override<
      EventsFor<StripePaymentElement>,
      {
        class?: string;
        onChange(event: StripePaymentElementChangeEvent): void;
      }
    >;
  }

  export function PaymentElement(props: D<PaymentElement.Props>) {
    const rect = createDomRect({ only: ["size"] });

    return (
      <div
        class={c`transition-normal ${props.class}`}
        style={{ height: UString.withAllValues`${rect()?.height}px` }}
      >
        {(rect()?.height || 0) < 8 && <Spinner.Common />}
        <div
          ref={(ref) => {
            mountStripeElement("payment", {}, (callbackName, e) =>
              props[callbackName]?.(e)
            )(ref);
            rect.ref(ref);
          }}
        />
      </div>
    );
  }
  PaymentElement.confirmSetup = async (owner: Owner) =>
    runWithOwner(owner, async () => {
      const stripe = useContext();
      const elements = Elements.useContext();

      if (!elements())
        throw new Error(
          "Elements must be loaded before calling Stripe.PaymentElement.confirmSetup."
        );

      elements()!.submit();
      return (await stripe.load()).confirmSetup({
        elements: elements()!,
        clientSecret: elements.clientSecret()!,
        redirect: "if_required",
      });
    })!;
}

export default Stripe;
